import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, Supplier, ConsignmentItem, ItemCreate, Product, ConsignmentOverview, ConsignmentOverviewSupplier } from "../api";
import {
  GlassPanel,
  KpiCard,
  DataTable,
  Modal,
  SkeletonTable,
  ErrorState,
  EmptyState,
  toast,
  confirm,
} from "../components";
import { formatVND, formatNumber, formatDate } from "../utils";
import type { Column } from "../components/DataTable";
import { ConsignmentOverviewChart } from "../components/ConsignmentOverviewChart";

// Row gộp: thông tin quản lý (tồn/giá) + số liệu bán trong kỳ
// shop_profit = revenue - supplier_payable (server tính, dùng basis_price nên tăng giá bán chỉ tăng lời shop)
type Row = ConsignmentItem & { qty_sold: number; revenue: number; supplier_payable: number; shop_profit: number };

// YYYY-MM-DD đầu tháng hiện tại (local)
function monthStartStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

// YYYY-MM-DD đầu năm hiện tại (local)
function yearStartStr() {
  return `${new Date().getFullYear()}-01-01`;
}

const escHtml = (s: string) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const vnd = (n: number) => `${Math.round(n || 0).toLocaleString("vi-VN")}đ`;

// dd/mm/yyyy từ ISO/date string; "—" nếu rỗng
const ddmmyyyy = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/* ── HTML báo cáo ký gửi (in/PDF qua window.print) ── */
function buildConsignmentReportHtml(opts: {
  supplierName: string;
  from: string;
  to: string;
  rows: Row[];
  totals: { payable: number; profit: number; revenue: number };
  discount: number;
  shopName: string;
}): string {
  const { supplierName, from, to, rows, totals, discount, shopName } = opts;
  const now = new Date();
  const exportedAt = now.toLocaleString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  // Lời hiển thị cho NCC = (giá ký gửi − tiền trả NCC), KHÔNG dùng giá bán thật.
  // Giá bán cao hơn basis là phần chênh của shop, không đưa vào file gửi NCC.
  const pdfRowProfit = (r: Row) => {
    const basis = Number(r.basis_price);
    if (!(Number.isFinite(basis) && basis > 0)) return r.revenue - r.supplier_payable;
    return Math.round(r.qty_sold * basis) - r.supplier_payable;
  };
  const pdfProfit = rows.reduce((s, r) => s + pdfRowProfit(r), 0);
  // Doanh thu PDF = Σ(giá ký gửi × SL bán) = Phải trả NCC + Shop lời. KHÔNG dùng giá bán thật
  // (495.000 − 273.000 sẽ lộ phần chênh 25k của shop).
  const pdfRevenue = pdfProfit + totals.payable;

  const detailRows = rows.map((r) => {
    const importDate = r.import_date || r.item_created_at;
    const rowProfit = pdfRowProfit(r);
    return `<tr>
      <td class="l">${escHtml(r.item_name || "—")}</td>
      <td class="l">${escHtml(r.sku || "—")}</td>
      <td class="r">${r.basis_price ? vnd(r.basis_price) : "—"}</td>
      <td class="r">${(discount || 0).toLocaleString("vi-VN")}%</td>
      <td class="r">${(r.in_stock ?? 0).toLocaleString("vi-VN")}</td>
      <td class="r">${r.import_qty != null ? r.import_qty.toLocaleString("vi-VN") : "—"}</td>
      <td class="c">${ddmmyyyy(importDate)}</td>
      <td class="r">${r.qty_sold > 0 ? r.qty_sold.toLocaleString("vi-VN") : "—"}</td>
      <td class="r">${r.supplier_payable > 0 ? vnd(r.supplier_payable) : "—"}</td>
      <td class="r">${r.qty_sold > 0 ? vnd(rowProfit) : "—"}</td>
    </tr>`;
  }).join("");

  // <title> = tên file mặc định khi "Lưu thành PDF" → đặt theo NCC để dễ lưu/đối soát.
  const docTitle = supplierName ? `Hàng ký gửi - ${supplierName}` : "Báo cáo hàng ký gửi";
  return `<!DOCTYPE html>
<html lang="vi"><head><meta charset="utf-8"><title>${escHtml(docTitle)}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; color: #1a1a1a;
    font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; text-align: center; letter-spacing: 0.5px; }
  .meta { text-align: center; color: #555; font-size: 12px; margin-bottom: 16px; line-height: 1.5; }
  .meta strong { color: #1a1a1a; }
  h2 { font-size: 13px; margin: 18px 0 6px; padding-bottom: 3px; border-bottom: 2px solid #333; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
  th, td { border: 1px solid #999; padding: 5px 7px; vertical-align: middle; }
  th { background: #2c3e50; color: #fff; font-weight: 600; text-align: center; font-size: 11px; }
  td.l { text-align: left; } td.r { text-align: right; font-variant-numeric: tabular-nums; } td.c { text-align: center; }
  .summary td { font-size: 13px; }
  .summary .lbl { background: #f0f3f6; font-weight: 600; width: 33%; }
  .summary .val { text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; }
  .summary .val.warn { color: #b8860b; } .summary .val.ok { color: #1f8a3b; } .summary .val.blue { color: #1565c0; }
  .detail th, .detail td { font-size: 11px; }
  .detail tbody tr:nth-child(even) { background: #f7f9fb; }
  tfoot td { font-weight: 700; background: #eef2f6; }
  .footer { margin-top: 20px; display: flex; justify-content: space-between; color: #666; font-size: 11px; }
  @media print { tr { page-break-inside: avoid; } thead { display: table-header-group; } }
</style></head>
<body>
  <h1>BÁO CÁO HÀNG KÝ GỬI</h1>
  <div class="meta">
    Nhà cung cấp: <strong>${escHtml(supplierName || "—")}</strong><br/>
    Kỳ đối soát: <strong>${ddmmyyyy(from)}</strong> &rarr; <strong>${ddmmyyyy(to)}</strong>
  </div>

  <h2>Tổng hợp kỳ</h2>
  <table class="summary">
    <tr><td class="lbl">Tổng SL đã bán (kỳ)</td><td class="val blue">${rows.reduce((s, r) => s + (r.qty_sold > 0 ? r.qty_sold : 0), 0).toLocaleString("vi-VN")}</td></tr>
    <tr><td class="lbl">Doanh thu (kỳ)</td><td class="val blue">${vnd(pdfRevenue)}</td></tr>
    <tr><td class="lbl">Shop lời (kỳ)</td><td class="val ok">${vnd(pdfProfit)}</td></tr>
    <tr><td class="lbl">Phải trả NCC (kỳ)</td><td class="val warn">${vnd(totals.payable)}</td></tr>
  </table>

  <h2>Chi tiết sản phẩm (${rows.length} SP)</h2>
  <table class="detail">
    <thead><tr>
      <th>Tên SP</th><th>SKU</th><th>Giá ký gửi</th><th>CK (%)</th><th>Tồn</th><th>SL nhập</th>
      <th>Ngày nhập</th><th>Đã bán (kỳ)</th><th>Phải trả NCC</th><th>Shop lời</th>
    </tr></thead>
    <tbody>${detailRows || `<tr><td colspan="10" class="c">Không có dữ liệu</td></tr>`}</tbody>
    <tfoot><tr>
      <td colspan="8" class="r">Tổng cộng</td>
      <td class="r">${vnd(totals.payable)}</td>
      <td class="r">${vnd(pdfProfit)}</td>
    </tr></tfoot>
  </table>

  <div class="footer">
    <span>Ngày xuất: ${exportedAt}</span>
    <span>${escHtml(shopName)}</span>
  </div>
</body></html>`;
}

export default function ConsignmentPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  // Thứ tự rows đang hiển thị trên bảng (sau khi DataTable sort) — dùng cho xuất PDF.
  const [displayRows, setDisplayRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState({ payable: 0, profit: 0, revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tổng quan multi-NCC (độc lập supplierId) — load theo from/to
  const [overview, setOverview] = useState<ConsignmentOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  // Chiết khấu NCC (% shop ăn). Giá nhập = giá bán × (1 - disc/100).
  const [discount, setDiscount] = useState<number>(30);
  const [discountInput, setDiscountInput] = useState<string>("30");
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Sửa tại chỗ (giống ProductsPage) — đồng bộ thẳng SP gốc trên Loyverse.
  const [editingCell, setEditingCell] = useState<
    { id: number; field: "sell_price" | "in_stock" | "import_date" | "import_qty"; value: string } | null
  >(null);

  // Khoảng đối soát — mặc định "tháng này" để thấy ngay, không cần bấm nút
  const [from, setFrom] = useState(yearStartStr);
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Modal
  const [showAdd, setShowAdd] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);

  const reloadSuppliers = useCallback(async (selectId?: string) => {
    const r = await api.suppliers.list("consignment");
    setSuppliers(r.suppliers || []);
    if (selectId) setSupplierId(selectId);
  }, []);

  useEffect(() => {
    api.suppliers.list("consignment").then((r) => setSuppliers(r.suppliers || [])).catch(() => {});
  }, []);

  const loadOverview = useCallback(async (f: string, t: string) => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const res = await api.consignmentItems.overview(f, t);
      setOverview(res);
    } catch (e: any) {
      setOverviewError(e.message || "Lỗi tải tổng quan");
      setOverview(null);
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const loadData = useCallback(async (sid: string, f: string, t: string) => {
    if (!sid) { setRows([]); setTotals({ payable: 0, profit: 0, revenue: 0 }); return; }
    setLoading(true);
    setError(null);
    try {
      // Quản lý (tồn/giá) + đối soát (đã bán/phải trả) song song, merge theo item_id
      const [listRes, report] = await Promise.all([
        api.consignmentItems.list(sid),
        api.consignmentItems.report(sid, f, t),
      ]);
      const sales = new Map(report.rows.map((r) => [r.item_id, r]));
      const merged: Row[] = (listRes.items || []).map((it) => {
        const s = sales.get(it.item_id);
        return {
          ...it,
          qty_sold: s?.qty_sold ?? 0,
          revenue: s?.revenue ?? 0,
          supplier_payable: s?.supplier_payable ?? 0,
          shop_profit: s?.shop_profit ?? 0,
          unit_cost: s?.unit_cost ?? 0,
        };
      });
      setRows(merged);
      const d = listRes.discount_rate ?? 30;
      setDiscount(d);
      setDiscountInput(String(d));
      setTotals({
        payable: report.total_supplier_payable,
        profit: report.total_shop_profit,
        revenue: report.total_revenue,
      });
    } catch (e: any) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(from, to); }, [from, to, loadOverview]);
  useEffect(() => { loadData(supplierId, from, to); }, [supplierId, from, to, loadData]);

  const handleRemove = async (id: number) => {
    try {
      await api.consignmentItems.remove(id);
      toast.success("Đã gỡ SP khỏi ký gửi");
      setRows((prev) => prev.filter((i) => i.id !== id));
      loadOverview(from, to);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // In tem toàn bộ SP ký gửi đang hiển thị (NCC hiện tại) → BarcodePrintPage preselect.
  // qty = in_stock (số tem = tồn hiện tại); skip SP tồn <= 0.
  const handlePrintAllLabels = () => {
    if (!rows.length) { toast.error("Không có SP để in tem"); return; }
    const items = rows
      .filter((r) => r.item_id && Number(r.in_stock) > 0)
      .map((r) => ({ id: r.item_id, qty: Math.max(1, Math.floor(Number(r.in_stock) || 0)) }));
    if (!items.length) { toast.error("Không có SP còn tồn để in tem"); return; }
    sessionStorage.setItem("barcode_preselect", JSON.stringify(items));
    navigate("/ops/barcode");
  };

  // Lưu chiết khấu NCC → backend recalc cost toàn bộ SP + push Loyverse.
  const saveDiscount = async () => {
    const d = Number(discountInput);
    if (Number.isNaN(d) || d < 0 || d > 100) { toast.error("Chiết khấu 0–100%"); setDiscountInput(String(discount)); return; }
    if (d === discount) return;
    setSavingDiscount(true);
    try {
      const res = await api.suppliers.setDiscount(supplierId, d);
      setDiscount(d);
      toast.success(`Chiết khấu ${d}% — cập nhật giá nhập ${res.recalced} SP${res.pushed ? `, đẩy Loyverse ${res.pushed}` : ""}`);
      if (res.push_errors?.length) toast.error(`${res.push_errors.length} SP đẩy Loyverse lỗi`);
      loadData(supplierId, from, to);
    } catch (e: any) {
      toast.error(e.message);
      setDiscountInput(String(discount));
    } finally {
      setSavingDiscount(false);
    }
  };

  // Sửa Giá bán / Tồn tại chỗ → đồng bộ thẳng SP gốc (Loyverse + D1).
  const startEdit = (id: number, field: "sell_price" | "in_stock" | "import_qty", current: number) =>
    setEditingCell({ id, field, value: String(current ?? 0) });

  // Sửa Ngày nhập (import_date) → PATCH /api/items. Chuẩn hoá về YYYY-MM-DD cho input[type=date].
  const startEditDate = (id: number, current?: string | null) =>
    setEditingCell({ id, field: "import_date", value: (current || "").slice(0, 10) });

  const saveEdit = async () => {
    if (!editingCell) return;
    const { id, field, value } = editingCell;
    const row = rows.find((r) => r.id === id);
    setEditingCell(null);
    if (!row) return;

    // Ngày nhập: lưu thẳng vào items.import_date, không cần variant_id.
    if (field === "import_date") {
      const cur = (row.import_date || row.item_created_at || "").slice(0, 10);
      if (value === cur) return;
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, import_date: value, item_created_at: value } : r)));
      try {
        await api.items.update(row.item_id, { import_date: value });
        toast.success("Đã lưu ngày nhập");
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        loadData(supplierId, from, to);
      }
      return;
    }

    // SL nhập: lưu thẳng vào items.import_qty (không cần variant_id, độc lập tồn kho).
    if (field === "import_qty") {
      const cur = Number(row.import_qty ?? 0);
      const n = value === "" ? null : Math.max(0, Math.floor(Number(value) || 0));
      if (Number(n ?? 0) === cur) return;
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, import_qty: n } : r)));
      try {
        await api.items.update(row.item_id, { import_qty: n });
        toast.success("Đã lưu SL nhập");
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        loadData(supplierId, from, to);
      }
      return;
    }

    if (!row.variant_id) { toast.error("SP thiếu variant_id, không sửa được"); return; }
    const num = Number(value);
    if (Number.isNaN(num)) { toast.error("Giá trị không hợp lệ"); return; }

    try {
      if (field === "in_stock") {
        const delta = num - Number(row.in_stock ?? 0);
        if (delta === 0) return;
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, in_stock: num } : r)));
        await api.inventoryAdjust({ item_id: row.item_id, variant_id: row.variant_id, delta, reason: "ký gửi inline edit" });
        toast.success("Đã cập nhật tồn");
      } else {
        if (num === Number(row.sell_price ?? 0)) return;
        // Hai giá: sửa giá bán KHÔNG đụng tiền trả NCC — payable theo basis_price (giá ký gửi).
        // cost trên Loyverse vẫn đồng bộ theo discount để báo cáo ngoài kịch bản này vẫn ra số hợp lý.
        const newCost = Math.round(num * (100 - discount) / 100);
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, sell_price: num } : r)));
        await api.items.update(row.item_id, { variants: [{ variant_id: row.variant_id, default_price: num, cost: newCost }] });
        toast.success("Đã lưu giá bán — tiền trả NCC vẫn theo giá ký gửi");
      }
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật");
      loadData(supplierId, from, to);
    }
  };

  // Xoá hẳn NCC ngay tại trang ký gửi (giữ giá/cost/barcode/danh mục, chỉ gỡ link SP)
  const handleDeleteSupplier = async () => {
    const sup = suppliers.find((s) => s.id === supplierId);
    if (!sup) return;
    let msg = `Xoá NCC "${sup.name}"? Sẽ gỡ link SP nhưng GIỮ NGUYÊN giá, cost, barcode và danh mục trên Loyverse.`;
    try {
      const u = await api.suppliers.usage(sup.id);
      const n = Math.max(u.items_count, u.consignment_count);
      if (n > 0) msg = `Xoá NCC "${sup.name}"? Sẽ gỡ link ${n} SP nhưng GIỮ NGUYÊN giá, cost, barcode và danh mục.`;
    } catch { /* dùng msg mặc định */ }
    const ok = await confirm({ title: `Xoá NCC "${sup.name}"`, message: msg, danger: true, confirmLabel: "Xoá NCC" });
    if (!ok) return;
    try {
      const res = await api.suppliers.remove(sup.id);
      toast.success(res.unlinked ? `Đã xoá NCC, gỡ link ${res.unlinked} SP` : "Đã xoá NCC");
      setSupplierId("");
      setRows([]);
      const r = await api.suppliers.list("consignment");
      setSuppliers(r.suppliers || []);
      loadOverview(from, to);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // Xuất PDF báo cáo ký gửi → in qua iframe ẩn (window.print).
  const exportPdf = () => {
    if (!supplierId) { toast.error("Chọn NCC trước khi xuất"); return; }
    const supplierName = suppliers.find((s) => s.id === supplierId)?.name || "";
    // Xuất theo đúng thứ tự đang sort trên bảng (fallback rows nếu chưa có).
    const exportRows = displayRows.length ? displayRows : rows;
    const html = buildConsignmentReportHtml({ supplierName, from, to, rows: exportRows, totals, discount, shopName: "Loyverse POS" });
    // Chrome lấy TÊN FILE PDF từ document.title của TRANG CHÍNH (không phải iframe) khi in iframe ẩn.
    // → đổi tạm title trang chính theo NCC, khôi phục sau khi in.
    const fileTitle = supplierName ? `Hàng ký gửi - ${supplierName}` : "Báo cáo hàng ký gửi";
    const prevTitle = document.title;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const cleanup = () => { document.title = prevTitle; try { document.body.removeChild(iframe); } catch {} };
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) throw new Error("Không truy cập được iframe");
        document.title = fileTitle;
        win.focus();
        win.print();
        setTimeout(cleanup, 1000);
        toast.success("Đã mở hộp thoại in — chọn 'Lưu thành PDF'");
      } catch (e: any) {
        cleanup();
        toast.error("Lỗi xuất PDF: " + (e?.message || e));
      }
    };
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) { cleanup(); toast.error("Không tạo được tài liệu in"); return; }
    doc.open(); doc.write(html); doc.close();
  };

  // Cell sửa được: click → input số; Enter/blur lưu, Esc huỷ.
  const editCell = (r: Row, field: "sell_price" | "in_stock" | "import_qty", display: string) => {
    const isEditing = editingCell?.id === r.id && editingCell?.field === field;
    if (isEditing) {
      return (
        <input
          autoFocus
          type="number"
          className="form-input"
          style={{ width: 100, textAlign: "right", padding: "2px 6px", marginLeft: "auto" }}
          value={editingCell!.value}
          onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            else if (e.key === "Escape") setEditingCell(null);
          }}
        />
      );
    }
    return (
      <span
        onClick={() => startEdit(r.id, field, Number(r[field] ?? 0))}
        style={{ cursor: "pointer", borderBottom: "1px dashed var(--border-subtle)" }}
        title="Bấm để sửa — đồng bộ thẳng SP gốc"
      >
        {display}
      </span>
    );
  };

  // Cell ngày nhập: click → input[type=date]; Enter/blur lưu, Esc huỷ.
  const editDateCell = (r: Row) => {
    const isEditing = editingCell?.id === r.id && editingCell?.field === "import_date";
    if (isEditing) {
      return (
        <input
          autoFocus
          type="date"
          className="form-input"
          style={{ width: 140, padding: "2px 6px" }}
          value={editingCell!.value}
          onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            else if (e.key === "Escape") setEditingCell(null);
          }}
        />
      );
    }
    return (
      <span
        onClick={() => startEditDate(r.id, r.import_date || r.item_created_at)}
        style={{ cursor: "pointer", borderBottom: "1px dashed var(--border-subtle)" }}
        title="Bấm để sửa ngày nhập"
      >
        {r.item_created_at ? formatDate(r.item_created_at) : "—"}
      </span>
    );
  };

  const columns: Column<Row>[] = [
    { key: "item_name", header: "Tên SP", sortable: true, render: (r) => r.item_name || "—" },
    { key: "sku", header: "SKU", sortable: true, render: (r) => r.sku || "—" },
    { key: "basis_price", header: "Giá ký gửi", align: "right", sortable: true, render: (r) => r.basis_price ? formatVND(r.basis_price) : "—" },
    { key: "sell_price", header: "Giá bán", align: "right", sortable: true, render: (r) => editCell(r, "sell_price", r.sell_price ? formatVND(r.sell_price) : "—") },
    { key: "unit_cost", header: "Trả NCC/cái", align: "right", sortable: true, render: (r) => r.unit_cost ? formatVND(r.unit_cost) : "—" },
    { key: "in_stock", header: "Tồn", align: "right", sortable: true, render: (r) => editCell(r, "in_stock", formatNumber(r.in_stock ?? 0)) },
    { key: "import_qty", header: "SL nhập", align: "right", sortable: true, render: (r) => editCell(r, "import_qty", r.import_qty != null ? formatNumber(r.import_qty) : "—") },
    { key: "item_created_at", header: "Ngày nhập", sortable: true, render: (r) => editDateCell(r) },
    { key: "stock_updated_at", header: "Cập nhật", sortable: true, render: (r) => r.stock_updated_at ? formatDate(r.stock_updated_at) : "—" },
    { key: "qty_sold", header: "Đã bán (kỳ)", align: "right", sortable: true, render: (r) => r.qty_sold > 0 ? formatNumber(r.qty_sold) : "—" },
    { key: "revenue", header: "Doanh thu", align: "right", sortable: true, render: (r) => r.revenue > 0 ? formatVND(r.revenue) : "—" },
    { key: "supplier_payable", header: "Phải trả NCC", align: "right", sortable: true, render: (r) => r.supplier_payable > 0 ? formatVND(r.supplier_payable) : "—" },
    { key: "shop_profit", header: "Shop lời", align: "right", sortable: true, render: (r) => r.shop_profit !== 0 ? formatVND(r.shop_profit) : "—" },
    {
      key: "actions", header: "",
      render: (r) => (
        <button className="btn-icon danger" onClick={() => handleRemove(r.id)} title="Gỡ SP">
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
        </button>
      ),
    },
  ];

  const overviewColumns: Column<ConsignmentOverviewSupplier>[] = [
    {
      key: "supplier_name", header: "NCC", sortable: true,
      render: (r) => (
        <button
          type="button"
          onClick={() => setSupplierId(r.supplier_id)}
          style={{ background: "none", border: "none", padding: 0, color: "var(--accent, #a78bfa)", cursor: "pointer", font: "inherit", textAlign: "left" }}
          title="Xem chi tiết NCC"
        >
          {r.supplier_name || "—"}
        </button>
      ),
    },
    { key: "item_count", header: "SL SP", align: "right", sortable: true, render: (r) => formatNumber(r.item_count ?? 0) },
    { key: "qty_sold", header: "Đã bán", align: "right", sortable: true, render: (r) => r.qty_sold > 0 ? formatNumber(r.qty_sold) : "—" },
    { key: "revenue", header: "Doanh thu", align: "right", sortable: true, render: (r) => r.revenue > 0 ? formatVND(r.revenue) : "—" },
    { key: "supplier_payable", header: "Phải trả", align: "right", sortable: true, render: (r) => r.supplier_payable > 0 ? formatVND(r.supplier_payable) : "—" },
    { key: "shop_profit", header: "Shop lời", align: "right", sortable: true, render: (r) => r.shop_profit > 0 ? formatVND(r.shop_profit) : "—" },
    {
      key: "actions", header: "",
      render: (r) => (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSupplierId(r.supplier_id)}
          title="Xem chi tiết NCC"
        >
          Xem
        </button>
      ),
    },
  ];

  const ov = overview?.totals;
  const hasNoSuppliers = !overviewLoading && !overviewError && overview && (ov?.supplier_count ?? 0) === 0;
  const hasNoSales = !overviewLoading && !overviewError && overview && (ov?.supplier_count ?? 0) > 0 && (ov?.suppliers_with_sales ?? 0) === 0 && (ov?.revenue ?? 0) === 0;

  return (
    <>
      {/* Explainer */}
      <div style={{ padding: "10px 14px", marginBottom: 16, background: "rgba(167,139,250,0.08)", borderLeft: "3px solid #a78bfa", borderRadius: 6, fontSize: 13, lineHeight: 1.6 }}>
        Hàng ký gửi: tổng quan mọi NCC theo kỳ, rồi drill-down gán/gỡ SP và đối soát. Số liệu "Đã bán / Phải trả" theo khoảng ngày bên dưới.
      </div>

      {/* Bộ lọc: NCC + khoảng ngày đối soát */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div style={{ flex: "0 0 280px" }}>
          <label className="form-label">Nhà cung cấp</label>
          <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
            <select
              className="form-input"
              style={{ flex: 1 }}
              value={supplierId}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setShowAddSupplier(true);
                  return;
                }
                setSupplierId(e.target.value);
              }}
            >
              <option value="">-- Chọn NCC --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
              <option value="__new__">+ Tạo NCC mới...</option>
            </select>
            <button
              className="btn-icon"
              onClick={() => setShowAddSupplier(true)}
              title="Tạo NCC mới"
              style={{ flexShrink: 0 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
            </button>
            {supplierId && (
              <button
                className="btn-icon danger"
                onClick={handleDeleteSupplier}
                title="Xoá NCC này"
                style={{ flexShrink: 0 }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: "0 0 140px" }}>
          <label className="form-label">Từ ngày</label>
          <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={{ flex: "0 0 140px" }}>
          <label className="form-label">Đến ngày</label>
          <input type="date" className="form-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div style={{ flex: "0 0 auto" }}>
          <label className="form-label">&nbsp;</label>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(monthStartStr()); setTo(new Date().toISOString().slice(0, 10)); }} title="Đặt khoảng: ngày 1 tháng này → hôm nay">
              Tháng này
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setFrom(`${new Date().getFullYear()}-01-01`); setTo(new Date().toISOString().slice(0, 10)); }} title="Đặt khoảng: 01/01 năm nay → hôm nay">
              Năm này
            </button>
          </div>
        </div>
        {supplierId && (
          <div style={{ flex: "0 0 220px" }}>
            <label className="form-label">Chiết khấu NCC (%)</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="number"
                min={0}
                max={100}
                className="form-input"
                style={{ flex: 1, minWidth: 90 }}
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveDiscount()}
                title="% shop ăn. Giá nhập = giá bán × (1 − %). Lưu sẽ tính lại giá nhập mọi SP + đẩy Loyverse."
              />
              <button className="btn btn-primary" onClick={saveDiscount} disabled={savingDiscount || Number(discountInput) === discount} style={{ flexShrink: 0 }}>
                {savingDiscount ? "..." : "Lưu"}
              </button>
            </div>
          </div>
        )}
      </div>

      {!supplierId ? (
        <>
          {!overviewLoading && !overviewError && ov && (ov.supplier_count > 0) && (
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <KpiCard label="Tổng doanh thu" value={formatVND(ov.revenue)} dotColor="blue" />
              <KpiCard label="Phải trả NCC" value={formatVND(ov.supplier_payable)} dotColor="warn" />
              <KpiCard label="Shop lời" value={formatVND(ov.shop_profit)} dotColor="ok" />
              <KpiCard label="NCC có bán" value={formatNumber(ov.suppliers_with_sales)} note={`${ov.supplier_count} NCC · ${formatNumber(ov.item_count)} SP`} />
            </div>
          )}

          <GlassPanel
            title="Doanh thu theo ngày · từng NCC"
            badge="biểu đồ"
            badgeVariant="info"
            style={{ marginBottom: 16 }}
          >
            <ConsignmentOverviewChart from={from} to={to} />
          </GlassPanel>

          <GlassPanel
            title="Tổng quan NCC ký gửi"
            badge={overview ? `${overview.suppliers?.length ?? 0} NCC` : undefined}
            badgeVariant="info"
          >
            {overviewLoading ? (
              <SkeletonTable rows={4} cols={7} />
            ) : overviewError ? (
              <ErrorState error={overviewError} onRetry={() => loadOverview(from, to)} />
            ) : hasNoSuppliers ? (
              <EmptyState
                icon="storefront"
                title="Chưa có NCC ký gửi"
                description="Tạo NCC mới hoặc chọn '+ Tạo NCC mới...' ở bộ lọc phía trên."
              />
            ) : (
              <>
                {hasNoSales && (
                  <div style={{ marginBottom: 12, fontSize: 13, opacity: 0.75 }}>
                    Không có bán trong kỳ — vẫn liệt kê NCC có SP ký gửi.
                  </div>
                )}
                <div className="table-wrap">
                  <DataTable
                    columns={overviewColumns}
                    rows={overview?.suppliers || []}
                    rowKey={(r) => r.supplier_id}
                    emptyMessage="Không có NCC"
                    multiSort
                  />
                </div>
              </>
            )}
          </GlassPanel>
        </>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setSupplierId("")}
              title="Quay lại bảng tổng quan mọi NCC"
            >
              ← Tổng quan
            </button>
          </div>

          {/* KPI đối soát kỳ */}
          {!loading && !error && rows.length > 0 && (
            <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <KpiCard label="Phải trả NCC (kỳ)" value={formatVND(totals.payable)} dotColor="warn" />
              <KpiCard label="Shop lời (kỳ)" value={formatVND(totals.profit)} dotColor="ok" />
              <KpiCard label="Doanh thu (kỳ)" value={formatVND(totals.revenue)} dotColor="blue" />
            </div>
          )}

          {/* Items table */}
          <GlassPanel
            title="SP ký gửi"
            badge={`${rows.length} SP`}
            badgeVariant="info"
          >
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handlePrintAllLabels}
                disabled={!rows.length}
                title="In tem mã vạch cho tất cả SP ký gửi của NCC này"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>qr_code_2</span>
                In tem
              </button>
              <button className="btn btn-secondary btn-sm" onClick={exportPdf} disabled={rows.length === 0} title="Xuất báo cáo ký gửi ra PDF">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>picture_as_pdf</span>
                Xuất PDF
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                Thêm SP ký gửi
              </button>
            </div>
            {loading ? (
              <SkeletonTable rows={4} cols={9} />
            ) : error ? (
              <ErrorState error={error} onRetry={() => loadData(supplierId, from, to)} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon="inventory_2"
                title="Chưa có SP ký gửi"
                description="Bấm nút '+ Thêm SP ký gửi' để gán sản phẩm cho NCC này."
              />
            ) : (
              <div className="table-wrap">
                <DataTable columns={columns} rows={rows} rowKey={(r) => String(r.id)} emptyMessage="" multiSort onSortedRowsChange={setDisplayRows} />
              </div>
            )}
          </GlassPanel>
        </>
      )}

      {/* Add modal */}
      {showAdd && (
        <AddItemModal
          supplierId={supplierId}
          supplierName={suppliers.find((s) => s.id === supplierId)?.name || ""}
          existingItemIds={new Set(rows.map((i) => i.item_id))}
          onClose={() => setShowAdd(false)}
          onAdded={() => { loadData(supplierId, from, to); loadOverview(from, to); setShowAdd(false); }}
        />
      )}

      {showAddSupplier && (
        <AddSupplierModal
          onClose={() => setShowAddSupplier(false)}
          onCreated={(id) => { reloadSuppliers(id); loadOverview(from, to); setShowAddSupplier(false); }}
        />
      )}
    </>
  );
}

// ── Modal: Tạo NCC mới ──────────────────────────────

function AddSupplierModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Cần nhập tên NCC");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.suppliers.create({ name: name.trim(), phone: phone.trim() || undefined, supplier_type: "consignment" });
      toast.success(`Đã tạo NCC "${name.trim()}"`);
      onCreated(res.id);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Tạo NCC mới" width={420}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Tên NCC *</label>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VD: KG - Anh Nam"
            autoFocus
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label className="form-label">Số điện thoại (tuỳ chọn)</label>
          <input
            className="form-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="VD: 0901234567"
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={submitting} style={{ width: "100%" }}>
          {submitting ? "Đang tạo..." : "Tạo NCC"}
        </button>
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════
// Modal: Thêm SP ký gửi
// ═══════════════════════════════════════════════════

type AddMode = "existing" | "category" | "new";

function AddItemModal({
  supplierId,
  supplierName,
  existingItemIds,
  onClose,
  onAdded,
}: {
  supplierId: string;
  supplierName: string;
  existingItemIds: Set<string>;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [mode, setMode] = useState<AddMode>("category");

  return (
    <Modal open onClose={onClose} title="Thêm SP ký gửi" width={640}>
      {/* Segmented control */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn ${mode === "category" ? "active" : ""}`} onClick={() => setMode("category")}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>category</span>
          Theo danh mục
        </button>
        <button className={`tab-btn ${mode === "existing" ? "active" : ""}`} onClick={() => setMode("existing")}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>search</span>
          SP có sẵn
        </button>
        <button className={`tab-btn ${mode === "new" ? "active" : ""}`} onClick={() => setMode("new")}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add_circle</span>
          Tạo SP mới
        </button>
      </div>

      {mode === "category" ? (
        <CategoryTab
          supplierId={supplierId}
          supplierName={supplierName}
          existingItemIds={existingItemIds}
          onAdded={onAdded}
        />
      ) : mode === "existing" ? (
        <ExistingItemTab supplierId={supplierId} existingItemIds={existingItemIds} onAdded={onAdded} />
      ) : (
        <NewItemTab supplierId={supplierId} onAdded={onAdded} />
      )}
    </Modal>
  );
}

// ── Tab: Theo danh mục ──────────────────────────────

function CategoryTab({
  supplierId,
  supplierName,
  existingItemIds,
  onAdded,
}: {
  supplierId: string;
  supplierName: string;
  existingItemIds: Set<string>;
  onAdded: () => void;
}) {
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const preselectedRef = useRef(false);

  const loadProducts = useCallback(async (cid: string) => {
    setCategoryId(cid);
    setSelected(new Set());
    if (!cid) { setProducts([]); return; }
    setLoading(true);
    try {
      const r = await api.products({ category_id: cid, limit: 500 });
      setProducts(r.products || []);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    preselectedRef.current = false;
    api.categories.list().then((r) => {
      const cats = r.categories || [];
      setCategories(cats);
      // Preselect "KG - {supplier.name}" when present
      if (!supplierName || preselectedRef.current) return;
      const target = `KG - ${supplierName}`.toLowerCase();
      const hit = cats.find((c) => c.name.trim().toLowerCase() === target)
        || cats.find((c) => c.name.trim().toLowerCase() === supplierName.trim().toLowerCase())
        || cats.find((c) => c.name.toLowerCase().includes(supplierName.trim().toLowerCase()) && c.name.toLowerCase().includes("kg"));
      if (hit) {
        preselectedRef.current = true;
        void loadProducts(hit.id);
      }
    }).catch(() => {});
  }, [supplierName, loadProducts]);

  // SP chưa có trong ký gửi → có thể chọn
  const selectable = products.filter((p) => !existingItemIds.has(p.id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = selectable.length > 0 && selected.size === selectable.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map((p) => p.id)));
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await api.consignmentItems.add({ item_ids: Array.from(selected), supplier_id: supplierId });
      toast.success(`Đã thêm ${selected.size} SP vào ký gửi`);
      onAdded();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label className="form-label">Danh mục</label>
        <select className="form-input" value={categoryId} onChange={(e) => loadProducts(e.target.value)}>
          <option value="">-- Chọn danh mục --</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <SkeletonTable rows={4} cols={4} />
      ) : !categoryId ? (
        <EmptyState icon="category" title="Chọn danh mục" description="Chọn danh mục ở trên để xem danh sách sản phẩm." />
      ) : products.length === 0 ? (
        <EmptyState icon="inventory_2" title="Không có SP" description="Danh mục này chưa có sản phẩm." />
      ) : (
        <>
          {/* Thanh header: chọn tất cả + đếm */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 4px 8px",
              borderBottom: "1px solid var(--border-subtle)",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              <strong style={{ color: "var(--text-primary)" }}>{selected.size}</strong>
              <span style={{ opacity: 0.6 }}> / {selectable.length} SP đã chọn</span>
            </span>
            <button
              type="button"
              onClick={toggleAll}
              disabled={selectable.length === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "none",
                border: "none",
                color: selectable.length === 0 ? "var(--text-dim)" : "var(--accent)",
                padding: "4px 6px",
                fontSize: 13,
                fontWeight: 500,
                cursor: selectable.length === 0 ? "default" : "pointer",
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                {allSelected ? "remove_done" : "done_all"}
              </span>
              {allSelected ? "Bỏ chọn" : "Chọn tất cả"}
            </button>
          </div>

          {/* List rows compact */}
          <div style={{ maxHeight: 320, overflowY: "auto", margin: "0 -4px 12px" }}>
            {products.map((p) => {
              const already = existingItemIds.has(p.id);
              const isSelected = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  role="checkbox"
                  aria-checked={already ? undefined : isSelected}
                  aria-disabled={already || undefined}
                  tabIndex={already ? -1 : 0}
                  onClick={() => !already && toggle(p.id)}
                  onKeyDown={(e) => {
                    if (already) return;
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(p.id); }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minHeight: 44,
                    padding: "6px 10px",
                    borderRadius: 8,
                    borderLeft: `2px solid ${isSelected ? "var(--accent)" : "transparent"}`,
                    background: isSelected ? "var(--accent-blue-bg)" : "transparent",
                    opacity: already ? 0.45 : 1,
                    cursor: already ? "default" : "pointer",
                    transition: "background 150ms ease, border-color 150ms ease",
                  }}
                  onMouseEnter={(e) => { if (!already && !isSelected) e.currentTarget.style.background = "var(--bg-card-hover)"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: 20,
                      flexShrink: 0,
                      color: already ? "var(--text-dim)" : isSelected ? "var(--accent)" : "var(--text-tertiary)",
                    }}
                  >
                    {already ? "check_circle" : isSelected ? "check_box" : "check_box_outline_blank"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--text-primary)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.item_name}
                    </div>
                    {p.sku && (
                      <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 1 }}>{p.sku}</div>
                    )}
                  </div>
                  <span style={{ fontSize: 13, color: "var(--text-secondary)", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                    {already ? "đã thêm" : p.default_price ? formatVND(p.default_price) : "—"}
                  </span>
                </div>
              );
            })}
          </div>

          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={submitting || selected.size === 0}
            style={{ width: "100%" }}
          >
            {submitting ? "Đang thêm..." : selected.size === 0 ? "Chọn SP để thêm" : `Thêm ${selected.size} SP vào NCC`}
          </button>
        </>
      )}
    </div>
  );
}

// ── Tab: SP có sẵn ──────────────────────────────────

function ExistingItemTab({
  supplierId,
  existingItemIds,
  onAdded,
}: {
  supplierId: string;
  existingItemIds: Set<string>;
  onAdded: () => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  const doSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      return;
    }
    const seq = ++seqRef.current;
    setSearching(true);
    try {
      // products joins variants → sku / default_price / cost (items.search does not)
      const r = await api.products({ q, limit: 40 });
      if (seq !== seqRef.current) return;
      setResults(r.products || []);
      setSearched(true);
    } catch {
      if (seq !== seqRef.current) return;
      setResults([]);
      setSearched(true);
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(() => { void doSearch(query); }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  const toggle = (id: string) => {
    if (existingItemIds.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      await api.consignmentItems.add({ item_ids: Array.from(selected), supplier_id: supplierId });
      toast.success(`Đã thêm ${selected.size} SP vào ký gửi`);
      onAdded();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          className="form-input"
          style={{ flex: 1 }}
          placeholder="Gõ ≥2 ký tự — tìm theo tên hoặc SKU..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (debounceRef.current) clearTimeout(debounceRef.current);
              void doSearch(query);
            }
          }}
          autoFocus
          aria-label="Tìm sản phẩm có sẵn"
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            void doSearch(query);
          }}
          disabled={searching || query.trim().length < 2}
          title="Tìm ngay"
        >
          {searching ? "..." : "Tìm"}
        </button>
      </div>

      {searching && results.length === 0 ? (
        <SkeletonTable rows={4} cols={3} />
      ) : searched && results.length === 0 ? (
        <EmptyState icon="search_off" title="Không tìm thấy" description={`Không có SP khớp "${query.trim()}".`} />
      ) : results.length > 0 ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 4px 8px",
              borderBottom: "1px solid var(--border-subtle)",
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              <strong style={{ color: "var(--text-primary)" }}>{selected.size}</strong>
              <span style={{ opacity: 0.6 }}> đã chọn · {results.length} kết quả</span>
            </span>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", margin: "0 -4px 12px" }}>
            {results.map((p) => {
              const already = existingItemIds.has(p.id);
              const isSelected = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  role="checkbox"
                  aria-checked={already ? undefined : isSelected}
                  aria-disabled={already || undefined}
                  tabIndex={already ? -1 : 0}
                  onClick={() => toggle(p.id)}
                  onKeyDown={(e) => {
                    if (already) return;
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(p.id); }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minHeight: 44,
                    padding: "6px 10px",
                    borderRadius: 8,
                    borderLeft: `2px solid ${isSelected ? "var(--accent)" : "transparent"}`,
                    background: isSelected ? "var(--accent-blue-bg)" : "transparent",
                    opacity: already ? 0.45 : 1,
                    cursor: already ? "default" : "pointer",
                    transition: "background 150ms ease, border-color 150ms ease",
                  }}
                  onMouseEnter={(e) => { if (!already && !isSelected) e.currentTarget.style.background = "var(--bg-card-hover)"; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: 20,
                      flexShrink: 0,
                      color: already ? "var(--text-dim)" : isSelected ? "var(--accent)" : "var(--text-tertiary)",
                    }}
                  >
                    {already ? "check_circle" : isSelected ? "check_box" : "check_box_outline_blank"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        color: "var(--text-primary)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.item_name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 1 }}>
                      {[p.sku, p.cost ? `nhập ${formatVND(p.cost)}` : null].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <span style={{ fontSize: 13, color: "var(--text-secondary)", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                    {already ? "đã thêm" : p.default_price ? formatVND(p.default_price) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <EmptyState icon="search" title="Tìm SP có sẵn" description="Gõ tên hoặc SKU (≥2 ký tự) — kết quả hiện ngay." />
      )}

      {selected.size > 0 && (
        <button className="btn btn-primary" onClick={handleAdd} disabled={submitting} style={{ width: "100%" }}>
          {submitting ? "Đang thêm..." : `Thêm ${selected.size} SP vào ký gửi NCC`}
        </button>
      )}
    </div>
  );
}

// ── Tab: Tạo SP mới ────────────────────────────────

function NewItemTab({ supplierId, onAdded }: { supplierId: string; onAdded: () => void }) {
  const [name, setName] = useState("");
  const [sellPrice, setSellPrice] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [sku, setSku] = useState("");
  const [initStock, setInitStock] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Auto-fill cost = sell × 0.7
  const handleSellChange = (val: string) => {
    setSellPrice(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0 && !costPrice) {
      setCostPrice(Math.round(num * 0.7).toString());
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const sell = parseFloat(sellPrice);
    if (!name.trim() || isNaN(sell) || sell <= 0) {
      toast.error("Cần nhập tên SP và giá bán hợp lệ");
      return;
    }
    const cost = parseFloat(costPrice) || Math.round(sell * 0.7);
    const stock = parseInt(initStock) || undefined;

    setSubmitting(true);
    try {
      const itemData: ItemCreate = {
        item_name: name.trim(),
        track_stock: true,
        variants: [{
          default_price: sell,
          cost,
          sku: sku.trim() || undefined,
          initial_stock: stock,
        }],
      };
      const created = await api.items.create(itemData);
      const newItemId = created.item?.id;
      if (!newItemId) throw new Error("Không tạo được SP");

      await api.consignmentItems.add({ item_ids: [newItemId], supplier_id: supplierId });
      toast.success(`Đã tạo "${name.trim()}" và thêm vào ký gửi`);
      onAdded();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleCreate}>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label className="form-label">Tên sản phẩm *</label>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: Áo thun trắng" />
        </div>
        <div>
          <label className="form-label">Giá bán *</label>
          <input className="form-input" inputMode="numeric" value={sellPrice} onChange={(e) => handleSellChange(e.target.value.replace(/[^\d]/g, ""))} placeholder="VD: 200000" />
        </div>
        <div>
          <label className="form-label">Giá nhập (mặc định = 70% giá bán)</label>
          <input className="form-input" inputMode="numeric" value={costPrice} onChange={(e) => setCostPrice(e.target.value.replace(/[^\d]/g, ""))} placeholder="Tự tính" />
        </div>
        <div>
          <label className="form-label">SKU (tuỳ chọn)</label>
          <input className="form-input" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="VD: AT-001" />
        </div>
        <div>
          <label className="form-label">Tồn ban đầu (tuỳ chọn)</label>
          <input className="form-input" inputMode="numeric" value={initStock} onChange={(e) => setInitStock(e.target.value.replace(/[^\d]/g, ""))} placeholder="VD: 10" />
        </div>
      </div>
      <button className="btn btn-primary" type="submit" disabled={submitting} style={{ width: "100%", marginTop: 16 }}>
        {submitting ? "Đang tạo..." : "Tạo SP & thêm vào ký gửi"}
      </button>
    </form>
  );
}
